<script setup lang="ts">
type StoreStockInformationProps = {
  store: SFCCStore
  position: number
  stock?: StockResult
  productStockConfig?: ProductStockConfig
  productStoreLocation?: ProductStoreLocation
}
const props = defineProps<StoreStockInformationProps>()
const parentLocation = inject('location', '')
const isCheckoutOrBag = parentLocation === LOCATIONS.checkOut || parentLocation === LOCATIONS.myBag
const i18n = useI18n()
const productStore = props.productStoreLocation ? useProductStore(props.productStoreLocation) : undefined
const { bopisEnabled } = useFeatureFlags()
// Same facts as useStockConfig, so this row reports the same block reasons and
// collect scenarios as the PDP delivery section. Store stock stays per surface.
const { collectView, resolveScenario } = useCollectScenario(props.productStoreLocation)

const storeStockLine = () => props.stock?.stores?.find((store) => store.store === props.store.id)?.stockLevels?.[0]

// BOPIS: item type steers lead-time messaging instead of blocking — the row
// shows when the item can be collected at this store.
const bopisStockLevelConfig = () => {
  // No size selected: no lead-time calculation, just whether the stock api returns any
  // stock.
  if (productStore?.isMutliSizeProductWithoutSelectedSize) {
    const quantity = storeStockLine()?.quantity ?? 0
    return quantity > 0
      ? {
          message: i18n.t('product_details_page.find_store.popup.stock_message.available'),
          color: AVAILABILITY_COLORS.IN_STOCK,
          additionalMessage: i18n.t('product_details_page.find_store.popup.stock_message.available_select_size'),
        }
      : {
          message: i18n.t('product_details_page.find_store.popup.stock_message.not_available'),
          color: AVAILABILITY_COLORS.OUT_OF_STOCK,
        }
  }

  // Fulfilment source `none` (AC1): the SKU or this store doesn't offer C&C —
  // no collect scenario or date. Same gate as useStockConfig.
  if (!props.store.c_clickAndCollectEnabled || !productStore?.selectedProduct?.c_availableForClickAndCollect) {
    return {
      message: i18n.t('product_details_page.find_store.popup.stock_message.not_available'),
      color: AVAILABILITY_COLORS.OUT_OF_STOCK,
    }
  }

  const scenario = resolveScenario(storeStockLine())

  if (scenario.kind === 'notAvailable') {
    return {
      message: i18n.t('product_details_page.find_store.popup.stock_message.not_available'),
      color: AVAILABILITY_COLORS.OUT_OF_STOCK,
    }
  }
  const message =
    scenario.kind === 'storeStock'
      ? i18n.t(getClickAndCollectMessageKey(props.store))
      : i18n.t('product_details_page.find_store.collect_by_date', {
          date: formatCollectionDate(addBusinessDays(new Date(), scenario.leadDays), i18n.locale.value),
        })
  return { message, color: AVAILABILITY_COLORS.IN_STOCK }
}

const stockLevelConfig = computed(() => {
  if (bopisEnabled()) return bopisStockLevelConfig()

  const reason = firstItemBlock(collectView())
  if (reason) {
    return { message: i18n.t(STORE_LINE_MESSAGE_KEY[reason]), color: AVAILABILITY_COLORS.OUT_OF_STOCK }
  }

  const quantity = storeStockLine()?.quantity ?? 0
  return quantity > 0
    ? {
        message: i18n.t('product_details_page.find_store.popup.stock_message.available'),
        color: AVAILABILITY_COLORS.IN_STOCK,
      }
    : {
        message: i18n.t('product_details_page.find_store.popup.stock_message.not_available'),
        color: AVAILABILITY_COLORS.OUT_OF_STOCK,
      }
})
</script>

<template>
  <div v-if="isCheckoutOrBag" class="store-stock-information">
    <span data-testid="store-stock-information__count" :style="productStockConfig?.style">
      {{
        $t('product_details_page.find_store.popup.stock_information', {
          availableNumberOfProduct: productStockConfig?.availableProducts,
          totalNumberOfProduct: productStockConfig?.totalProducts,
        })
      }}
    </span>
  </div>
  <div v-else-if="stock" class="find-in-store-popup__stock-information">
    <span data-testid="store-stock-information__message" :style="stockLevelConfig.color">
      {{ stockLevelConfig.message }}
    </span>
    <span
      v-if="stockLevelConfig?.additionalMessage"
      class="find-in-store-popup__stock-information__additional-message"
      data-testid="store-stock-information__message__additional-message"
    >
      {{ stockLevelConfig.additionalMessage }}
    </span>
  </div>
</template>

<style lang="postcss">
.find-in-store-item {
  .find-in-store-popup__stock-information__additional-message {
    color: var(--text-secondary);
    font-weight: normal;
  }
}
</style>
